// src/utils/improved-video-chunk-uploader.js
import axios from 'axios';

export class ImprovedVideoChunkUploader {
    constructor({ 
        sliceUrl, 
        statusUrl, 
        userId, 
        chunkSize = 2*1024*1024, 
        pollInterval = 2000,
        maxConcurrent = 2, // 视频文件较大，并发数稍低
        retryDelay = 1500,  // 视频重试延迟稍长
        maxRetries = 5      // 视频重试次数更多
    }) {
        Object.assign(this, { 
            sliceUrl, statusUrl, userId, chunkSize, pollInterval,
            maxConcurrent, retryDelay, maxRetries
        });
        this.uploadTasks = new Map();
        this.activeUploads = 0;
    }

    /**
     * 并发控制的视频分片上传
     * @param {File} file
     * @param {Function} onProgress
     * @param {string} uploadId
     * @param {Function} onStatusChange
     * @returns {Promise<any>}
     */
    async upload(file, onProgress, uploadId, onStatusChange) {
        const total = Math.ceil(file.size / this.chunkSize);
        const uploadTask = this.uploadTasks.get(uploadId);
        
        if (!uploadTask) {
            throw new Error('Upload task not found');
        }

        // 检查已上传的分片
        const uploadedChunks = await this.checkUploadedChunks(uploadTask.taskId);
        uploadedChunks.forEach(chunkIndex => {
            uploadTask.uploadedChunks.add(chunkIndex);
        });

        // 创建待上传的分片队列
        const pendingChunks = [];
        for (let index = 0; index < total; index++) {
            if (!uploadTask.uploadedChunks.has(index)) {
                pendingChunks.push(index);
            }
        }

        // 初始进度
        const initialProgress = Math.floor((uploadTask.uploadedChunks.size / total) * 100);
        onProgress(initialProgress, uploadTask.uploadedChunks.size, total);

        // 并发上传分片
        await this.uploadChunksConcurrently(file, pendingChunks, uploadTask, total, onProgress);

        // 轮询状态直到完成
        return this._pollStatus(uploadTask.taskId, onStatusChange);
    }

    /**
     * 并发上传视频分片
     * @param {File} file 
     * @param {number[]} pendingChunks 
     * @param {Object} uploadTask 
     * @param {number} total 
     * @param {Function} onProgress 
     */
    async uploadChunksConcurrently(file, pendingChunks, uploadTask, total, onProgress) {
        let chunkIndex = 0;
        const failedChunks = new Set();

        // 创建并发上传的Promise池
        const uploadNextChunk = async () => {
            while (chunkIndex < pendingChunks.length) {
                // 检查是否暂停或取消
                if (uploadTask.isPaused) {
                    await this.waitForResume(uploadTask);
                }
                
                if (uploadTask.isCancelled) {
                    throw new Error('Upload cancelled');
                }

                const index = pendingChunks[chunkIndex++];
                
                try {
                    // 限制并发数
                    while (this.activeUploads >= this.maxConcurrent) {
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }

                    this.activeUploads++;
                    await this.uploadSingleVideoChunk(file, index, uploadTask, total);
                    uploadTask.uploadedChunks.add(index);
                    uploadTask.successCount++;
                    
                    // 更新进度
                    const progress = Math.floor((uploadTask.uploadedChunks.size / total) * 100);
                    onProgress(progress, uploadTask.uploadedChunks.size, total);
                    
                } catch (error) {
                    console.error(`Failed to upload video chunk ${index}:`, error);
                    uploadTask.failureCount++;
                    failedChunks.add(index);
                    
                    // 如果失败率过高，降低并发数
                    const failureRate = uploadTask.failureCount / (uploadTask.successCount + uploadTask.failureCount);
                    if (failureRate > 0.3 && this.maxConcurrent > 1) {
                        this.maxConcurrent--;
                        console.log(`Video upload failure rate high, reduced concurrency to ${this.maxConcurrent}`);
                    }
                    
                    throw error;
                } finally {
                    this.activeUploads--;
                }
            }
        };

        // 启动并发上传
        const concurrentTasks = [];
        for (let i = 0; i < Math.min(this.maxConcurrent, pendingChunks.length); i++) {
            concurrentTasks.push(uploadNextChunk());
        }

        try {
            await Promise.all(concurrentTasks);
        } catch (error) {
            // 如果有失败的分片，重试一次
            if (failedChunks.size > 0) {
                console.log(`Retrying ${failedChunks.size} failed video chunks...`);
                const retryChunks = Array.from(failedChunks);
                await this.retryFailedChunks(file, retryChunks, uploadTask, total, onProgress);
            }
        }
    }

    /**
     * 重试失败的分片
     * @param {File} file 
     * @param {number[]} failedChunks 
     * @param {Object} uploadTask 
     * @param {number} total 
     * @param {Function} onProgress 
     */
    async retryFailedChunks(file, failedChunks, uploadTask, total, onProgress) {
        for (const index of failedChunks) {
            if (uploadTask.isCancelled) break;
            
            try {
                await this.uploadSingleVideoChunk(file, index, uploadTask, total);
                uploadTask.uploadedChunks.add(index);
                
                const progress = Math.floor((uploadTask.uploadedChunks.size / total) * 100);
                onProgress(progress, uploadTask.uploadedChunks.size, total);
            } catch (error) {
                console.error(`Retry failed for video chunk ${index}:`, error);
                // 最终失败，但不阻止其他分片
            }
        }
    }

    /**
     * 上传单个视频分片（带重试机制）
     * @param {File} file 
     * @param {number} index 
     * @param {Object} uploadTask 
     * @param {number} total 
     */
    async uploadSingleVideoChunk(file, index, uploadTask, total) {
        const start = index * this.chunkSize;
        const end = Math.min(start + this.chunkSize, file.size);
        const blob = file.slice(start, end);

        const form = new FormData();
        form.append('file', blob, file.name);
        form.append('taskId', uploadTask.taskId);
        form.append('index', index);
        form.append('total', total);
        form.append('filename', file.name);
        form.append('userId', this.userId);

        // 指数退避重试，视频文件重试更谨慎
        for (let attempt = 0; attempt < this.maxRetries; attempt++) {
            try {
                await axios.post(this.sliceUrl, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 60000 // 视频分片上传超时时间更长
                });
                return; // 成功上传，退出重试循环
            } catch (error) {
                if (attempt === this.maxRetries - 1) {
                    throw error; // 最后一次重试失败，抛出错误
                }
                
                // 指数退避 + 随机抖动，视频重试间隔更长
                const delay = this.retryDelay * Math.pow(2, attempt) + Math.random() * 2000;
                console.warn(`Video chunk ${index} upload failed, retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    /**
     * 等待恢复上传
     * @param {Object} uploadTask 
     */
    async waitForResume(uploadTask) {
        return new Promise(resolve => {
            const checkResume = setInterval(() => {
                if (!uploadTask.isPaused || uploadTask.isCancelled) {
                    clearInterval(checkResume);
                    resolve();
                }
            }, 200); // 视频检查间隔稍长
        });
    }

    // 其他方法保持与图片上传器类似
    generateTaskId(filename, fileSize) {
        return `video_${this.userId}_${filename}_${fileSize}`;
    }

    async createUploadTask(filename, fileSize) {
        const taskId = this.generateTaskId(filename, fileSize);
        const uploadTask = {
            taskId,
            filename,
            fileSize,
            uploadedChunks: new Set(),
            isPaused: false,
            isCancelled: false,
            startTime: Date.now(),
            successCount: 0,
            failureCount: 0
        };
        this.uploadTasks.set(taskId, uploadTask);
        return taskId;
    }

    async checkUploadedChunks(taskId) {
        try {
            const response = await axios.get(`${this.sliceUrl}/status`, {
                params: { taskId, userId: this.userId }
            });
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            console.error('Failed to check uploaded video chunks:', error);
            return [];
        }
    }

    async pauseUpload(uploadId) {
        const uploadTask = this.uploadTasks.get(uploadId);
        if (uploadTask) {
            uploadTask.isPaused = true;
        }
    }

    async resumeUpload(uploadId, onProgress) {
        const uploadTask = this.uploadTasks.get(uploadId);
        if (uploadTask) {
            uploadTask.isPaused = false;
            const total = Math.ceil(uploadTask.fileSize / this.chunkSize);
            const progress = Math.floor((uploadTask.uploadedChunks.size / total) * 100);
            onProgress(progress);
        }
    }

    async cancelUpload(uploadId) {
        const uploadTask = this.uploadTasks.get(uploadId);
        if (uploadTask) {
            uploadTask.isCancelled = true;
            this.uploadTasks.delete(uploadId);
        }
    }

    async _pollStatus(taskId, onStatusChange) {
        const params = { taskId, userId: this.userId };
        while (true) {
            const response = await axios.get(this.statusUrl, { params });
            const { data } = response;
            
            if (data.code === 200) {
                const status = data.data.status;
                if (onStatusChange) {
                    onStatusChange(status, {
                        origSize: data.data.origSize,
                        compSize: data.data.compSize
                    });
                }
                
                if (status === 'DONE' || status === 'FAILED') {
                    this.uploadTasks.delete(taskId);
                    return {
                        compressionSize: data.data.compUrl,
                        originalSize: data.data.origUrl,
                        progress: data.data.progress,
                        filename: data.data.filename,
                        origSize: data.data.origSize,
                        compSize: data.data.compSize,
                        status: status,
                        error: status === 'FAILED' ? data.data.errMsg : null
                    };
                }
            }
            
            if (data.code !== 200) {
                throw new Error(data.msg || '视频压缩任务失败');
            }
            
            await new Promise(r => setTimeout(r, this.pollInterval));
        }
    }
}